// Public upload portal transport. These endpoints sit under /api/public and
// return their DTOs raw, not inside the GenericResponse envelope the
// authenticated API uses, so every call reads response.data directly.

import axios from "const/axios";
import { API_URL } from "const/constants";

const publicApiBase = (API_URL || "").replace(/\/api\/v1\/?$/, "");

const requestBase = (token) => `${publicApiBase}/api/public/document-requests/${token}`;

/** The portal is unauthenticated. A staff bearer left on axios defaults must not ride along. */
const anonymous = (config = {}) => ({
  ...config,
  headers: { ...(config.headers || {}), Authorization: null },
});

const withSession = (accessToken, config = {}) =>
  anonymous({ ...config, headers: { ...(config.headers || {}), "X-Upload-Session": accessToken } });

const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

export const portalErrorMessage = (err) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || DEFAULT_ERROR_MESSAGE;

/** True when the backend says this link is revoked, submitted or simply unknown. */
export const isDeadLink = (err) => err?.response?.status === 404;

/**
 * True when the session has expired or was never valid, so the client must verify again. Every
 * such rejection is a 403: this is a public surface with no login, so a 401 is a server-side
 * fault and must surface its own message instead of sending the client round the OTP loop again.
 */
export const isSessionRejected = (err) => err?.response?.status === 403;

export const fetchLanding = (token) =>
  axios.get(requestBase(token), anonymous()).then((r) => r.data);

export const sendOtp = (token) =>
  axios.post(`${requestBase(token)}/otp/send`, null, anonymous()).then((r) => r.data);

export const verifyOtp = (token, code) =>
  axios.post(`${requestBase(token)}/otp/verify`, { code }, anonymous()).then((r) => r.data);

export const fetchSession = (token, accessToken) =>
  axios.get(`${requestBase(token)}/session`, withSession(accessToken)).then((r) => r.data);

export const uploadFile = (token, accessToken, itemId, file) => {
  const form = new FormData();
  form.append("file", file);
  return axios
    .post(`${requestBase(token)}/items/${itemId}/files`, form, withSession(accessToken))
    .then((r) => r.data);
};

export const fetchFileContent = (token, accessToken, documentId) =>
  axios
    .get(
      `${requestBase(token)}/files/${documentId}/content`,
      withSession(accessToken, { responseType: "blob" })
    )
    .then((r) => r.data);

export const removeFile = (token, accessToken, documentId) =>
  axios.delete(`${requestBase(token)}/files/${documentId}`, withSession(accessToken));

export const submitRequest = (token, accessToken) =>
  axios.post(`${requestBase(token)}/submit`, null, withSession(accessToken));
